import axios from "axios";

const CANONICAL_ENV_KEY = "REACT_APP_API_BASE_URL";
const LEGACY_ENV_KEY = "REACT_APP_BASE_URL";
// A deliberately tiny allowlist for the public RingOi application. Build-time
// configuration always wins; this only prevents a production build from
// silently making same-origin API calls when its environment variable was
// omitted. Unknown hosts still use the safe same-origin default below.
const TRUSTED_DEPLOYMENT_API_ORIGINS = Object.freeze({
  "https://global.ringoi.com": "https://global.backend.ringoi.com",
});
export const SESSION_USER_STORAGE_KEY = "user_token";
export const SESSION_CSRF_STORAGE_KEY = "crm_csrf_token";
export const AUTH_CACHE_STORAGE_KEY = "auth";
export const SESSION_CHANGED_EVENT = "crm-session-changed";
export const TENANT_MEMBERSHIP_AUTHORIZATION_SOURCE = "tenant_membership";

// These preferences are intentionally account-neutral. Every other local
// value is treated as potentially tenant-scoped when a workspace changes.
const TENANT_NEUTRAL_LOCAL_STORAGE_KEYS = new Set([
  "app_theme",
  "crm_last_email",
  "crm_sidebar_collapsed",
  "crm_sidebar_collapsed.v2",
  "crm_theme",
  "td_theme",
  "theme",
]);

const MUTATING_HTTP_METHODS = new Set(["post", "put", "patch", "delete"]);

/**
 * Normalizes an API origin or root-relative API path.
 *
 * Remote URLs are restricted to HTTP(S) and may not contain credentials,
 * query strings, or fragments. The returned value never has a trailing slash.
 */
export function normalizeApiBaseURL(value) {
  const raw = String(value ?? "").trim();

  if (!raw) return "";

  if (raw.startsWith("/")) {
    if (raw.startsWith("//") || raw.includes("?") || raw.includes("#")) {
      throw new TypeError("The API base path must be a root-relative path without a query or fragment.");
    }

    return raw.replace(/\/+$/, "");
  }

  let parsed;

  try {
    parsed = new URL(raw);
  } catch {
    throw new TypeError("The API base URL must be an absolute HTTP(S) URL or a root-relative path.");
  }

  if (!['http:', 'https:'].includes(parsed.protocol)) {
    throw new TypeError("The API base URL must use HTTP or HTTPS.");
  }

  if (parsed.username || parsed.password || parsed.search || parsed.hash) {
    throw new TypeError("The API base URL may not contain credentials, a query, or a fragment.");
  }

  const path = parsed.pathname.replace(/\/+$/, "");
  return `${parsed.origin}${path === "/" ? "" : path}`;
}

/**
 * REACT_APP_API_BASE_URL is the canonical setting. REACT_APP_BASE_URL remains
 * supported while existing environments are migrated.
 *
 * An absent setting intentionally resolves to the current origin (an empty
 * prefix). This fails locally at the deployed host instead of silently sending
 * development or staging data to a hardcoded production API.
 */
export function resolveApiBaseURL(environment = process.env) {
  const canonicalValue = String(environment?.[CANONICAL_ENV_KEY] ?? "").trim();
  const legacyValue = String(environment?.[LEGACY_ENV_KEY] ?? "").trim();
  return normalizeApiBaseURL(canonicalValue || legacyValue);
}

// CRA's browser ESLint environment does not declare ``globalThis``. Resolve
// from ``window`` lazily instead, which also keeps this module safe for Jest
// and server-side tooling where no browser global exists.
export function resolveTrustedDeploymentApiBaseURL(
  location = (typeof window === "undefined" ? undefined : window.location)
) {
  const origin = String(location?.origin || "").trim().replace(/\/+$/, "");
  return TRUSTED_DEPLOYMENT_API_ORIGINS[origin] || "";
}

function loadApiBaseURL() {
  try {
    return resolveApiBaseURL() || resolveTrustedDeploymentApiBaseURL();
  } catch (error) {
    // Keep requests on the current origin when configuration is invalid. This
    // is deliberately safer than falling back to any remote environment.
    console.error("Invalid CRM API base URL configuration; using same-origin requests.", error);
    return resolveTrustedDeploymentApiBaseURL();
  }
}

export const baseURL = loadApiBaseURL();

// Keep legacy direct axios calls cookie-ready while modules move to apiClient.
// The dedicated client is preferred for new and migrated API calls.
axios.defaults.withCredentials = true;

export const apiClient = axios.create({
  baseURL,
  withCredentials: true,
});

function getBrowserStorage(storageName) {
  if (typeof window === "undefined") return null;

  try {
    return window[storageName];
  } catch {
    return null;
  }
}

function dispatchSessionChanged(authenticated) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;

  try {
    window.dispatchEvent(
      new CustomEvent(SESSION_CHANGED_EVENT, { detail: { authenticated } })
    );
  } catch {
    // Storage remains the compatibility boundary in older browsers.
  }
}

function buildCachedUser(sessionPayload) {
  const user = sessionPayload?.user;

  if (!user || typeof user !== "object") {
    throw new TypeError("The authenticated session response is missing its user.");
  }

  const userEmail = String(user.email || "").trim().toLowerCase();
  const role = String(user.role || user.roles?.[0] || "User").trim() || "User";
  const tenantAuthorizationSource = String(
    sessionPayload?.tenant?.authorization_source || ""
  )
    .trim()
    .toLowerCase();

  if (!userEmail) {
    throw new TypeError("The authenticated session response is missing the user email.");
  }

  const permissions = sessionPayload.permissions;
  const dataScopes =
    sessionPayload.data_scopes && typeof sessionPayload.data_scopes === "object"
      ? { ...sessionPayload.data_scopes }
      : {};
  let access = {};

  if (
    permissions === "ALL" ||
    (tenantAuthorizationSource === "legacy_compatibility" && role === "SuperAdmin")
  ) {
    access = "ALL";
  } else if (permissions && typeof permissions === "object") {
    access = permissions;
  }

  return {
    userId: String(user.id || ""),
    userName: String(user.name || ""),
    userEmail,
    role,
    roles: Array.isArray(user.roles) ? user.roles : [role],
    access,
    permissions: access,
    dataScopes,
    tenantId: String(sessionPayload?.tenant?.id || ""),
    tenantDisplayName: String(sessionPayload?.tenant?.display_name || "").trim(),
    tenantSlug: String(sessionPayload?.tenant?.slug || "").trim(),
    tenantDefaultTimezone: String(
      sessionPayload?.tenant?.default_timezone || ""
    ).trim(),
    tenantDefaultLocale: String(
      sessionPayload?.tenant?.default_locale || ""
    ).trim(),
    tenantAuthorizationSource,
  };
}

/**
 * Cache the safe, server-derived principal for legacy UI permission checks.
 * This cache never grants access: ProtectedRoute revalidates the signed server
 * session before rendering any protected application content.
 */
export function cacheAuthoritativeSession(sessionPayload) {
  if (!sessionPayload || sessionPayload.authenticated === false) {
    throw new TypeError("The server did not return an authenticated session.");
  }

  const csrfToken = sessionPayload.csrf_token;
  if (typeof csrfToken !== "string" || !csrfToken.trim()) {
    throw new TypeError("The authenticated session response is missing its CSRF token.");
  }

  const cachedUser = buildCachedUser(sessionPayload);
  const sessionStore = getBrowserStorage("sessionStorage");
  const localStore = getBrowserStorage("localStorage");

  if (!sessionStore || !localStore) {
    throw new Error("Browser storage is unavailable for this authenticated session.");
  }

  try {
    sessionStore.setItem(SESSION_USER_STORAGE_KEY, JSON.stringify(cachedUser));
    sessionStore.setItem(SESSION_CSRF_STORAGE_KEY, csrfToken.trim());
    localStore.setItem(AUTH_CACHE_STORAGE_KEY, "true");
  } catch (error) {
    clearCachedSession();
    throw error;
  }

  dispatchSessionChanged(true);

  return cachedUser;
}

export function clearCachedSession() {
  const sessionStore = getBrowserStorage("sessionStorage");
  const localStore = getBrowserStorage("localStorage");

  try {
    sessionStore?.removeItem(SESSION_USER_STORAGE_KEY);
    sessionStore?.removeItem(SESSION_CSRF_STORAGE_KEY);
  } catch {
    // Logout navigation must continue even when browser storage is restricted.
  }

  try {
    localStore?.removeItem(AUTH_CACHE_STORAGE_KEY);
  } catch {
    // Server-side session revocation remains authoritative.
  }

  dispatchSessionChanged(false);
}

/**
 * Remove browser state that could have been derived from the previous tenant.
 *
 * A confirmed server-side workspace switch rotates the CSRF token and changes
 * the data security boundary. Clearing all session storage and all local values
 * except a very small account-neutral preference allowlist prevents drafts,
 * cached filters, identities, and module data from crossing that boundary.
 * The following hard reload rehydrates the new session from its HttpOnly cookie.
 */
export function clearTenantSensitiveBrowserCache({ notify = true } = {}) {
  const sessionStore = getBrowserStorage("sessionStorage");
  const localStore = getBrowserStorage("localStorage");

  try {
    sessionStore?.clear();
  } catch {
    // The reload must continue even when browser storage is restricted.
  }

  try {
    if (localStore) {
      const keys = Array.from(
        { length: localStore.length },
        (_, index) => localStore.key(index)
      ).filter(Boolean);
      keys.forEach((key) => {
        if (!TENANT_NEUTRAL_LOCAL_STORAGE_KEYS.has(key)) {
          localStore.removeItem(key);
        }
      });
    }
  } catch {
    // Server session membership remains authoritative if storage is blocked.
  }

  // A confirmed workspace switch immediately reloads into a new server-owned
  // session. Broadcasting a signed-out event during that narrow handoff made
  // the shell (including the navigation rail) disappear for one paint before
  // the reload. Callers can suppress only that cosmetic event while the
  // sensitive browser cache is still cleared synchronously.
  if (notify) {
    dispatchSessionChanged(false);
  }
}

export function getCachedSessionUser() {
  const sessionStore = getBrowserStorage("sessionStorage");
  try {
    const raw = sessionStore?.getItem(SESSION_USER_STORAGE_KEY);
    const value = raw ? JSON.parse(raw) : null;
    return value && typeof value === "object" ? value : null;
  } catch {
    return null;
  }
}

export function isMembershipBackedSession(user = getCachedSessionUser()) {
  return (
    String(user?.tenantAuthorizationSource || "").trim().toLowerCase() ===
    TENANT_MEMBERSHIP_AUTHORIZATION_SOURCE
  );
}

export function getSessionCsrfToken() {
  try {
    return getBrowserStorage("sessionStorage")?.getItem(SESSION_CSRF_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function resolveRequestURL(config) {
  const requestURL = String(config?.url || "").trim();
  const requestBaseURL = String(config?.baseURL ?? baseURL ?? "").trim();

  if (!requestURL) return null;

  const browserOrigin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "http://localhost";

  try {
    if (!requestBaseURL || /^https?:\/\//i.test(requestURL)) {
      return new URL(requestURL, browserOrigin);
    }

    // Axios combines a configured base path with a leading-slash request path
    // instead of treating the latter as origin-relative like the URL API does.
    const absoluteBaseURL = new URL(requestBaseURL, browserOrigin);
    const combinedPath = `${absoluteBaseURL.pathname.replace(/\/+$/, "")}/${requestURL.replace(/^\/+/, "")}`;
    return new URL(combinedPath, absoluteBaseURL.origin);
  } catch {
    return null;
  }
}

function isConfiguredApiRequest(config) {
  const resolvedRequest = resolveRequestURL(config);
  if (!resolvedRequest) return false;

  const browserOrigin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "http://localhost";

  try {
    const configuredApi = new URL(baseURL || "/", browserOrigin);
    if (resolvedRequest.origin !== configuredApi.origin) return false;

    const configuredPath = configuredApi.pathname.replace(/\/+$/, "");
    return !configuredPath || configuredPath === "/" ||
      resolvedRequest.pathname === configuredPath ||
      resolvedRequest.pathname.startsWith(`${configuredPath}/`);
  } catch {
    return false;
  }
}

/** Attach CSRF only to mutating requests sent to the configured CRM API. */
export function attachSessionCsrfToken(config = {}) {
  const method = String(config.method || "get").toLowerCase();
  if (!MUTATING_HTTP_METHODS.has(method) || !isConfiguredApiRequest(config)) {
    return config;
  }

  const csrfToken = getSessionCsrfToken();
  if (!csrfToken) return config;

  if (config.headers && typeof config.headers.set === "function") {
    config.headers.set("X-CSRF-Token", csrfToken);
  } else {
    config.headers = { ...(config.headers || {}), "X-CSRF-Token": csrfToken };
  }

  return config;
}

// apiClient is the preferred path. The global interceptor keeps the legacy
// direct axios calls protected while those modules are migrated incrementally.
apiClient.interceptors.request.use(attachSessionCsrfToken);
axios.interceptors.request.use(attachSessionCsrfToken);
